import { TEST_HUB_URL } from '@/constants/constants';
import { apiFetch } from '@/utils/apiWrapper';

const dashboardUrl = `${TEST_HUB_URL}cdk/dashboard/`;

export enum TimePeriod {
  TODAY = 'today',
  THIS_WEEK = 'this week',
  THIS_MONTH = 'this month',
  THIS_YEAR = 'this year',
  ALL_TIME = 'all_time',
}

// Company in top companies list
export interface TopCompany {
  _id: string;
  companyName: string;
  totalTransaction?: number;
  totalPaymentVolume?: number;
  currency?: string;
}

// Organization in top organizations list
export interface TopOrganization {
  _id: string;
  companyName: string;
  companyId: string;
  totalTransaction?: number;
  totalPaymentVolume?: number;
  currency?: string;
}

// Types for dashboard metrics data
export interface DashboardMetric {
  _id: string;
  companyId: string;
  numberOfOrganizations: number;
  numberOfCompanies: number;
  totalTransaction: number;
  totalPaymentVolume: number;
  currency?: string;
  topCompanies: TopCompany[];
  topOrganizations: TopOrganization[];
  orgOrder?: OrderMetricBy;
  companyOrder?: OrderMetricBy;
  orgDateRange?: TimePeriod;
  companyDateRange?: TimePeriod;
}

export enum OrderMetricBy {
  TOTAL_TRANSACTION = 'totalTransaction',
  TOTAL_PAYMENT_VOLUME = 'totalPaymentVolume',
}

export interface DashboardMetricParams {
  companyId: string;
  timePeriod?: TimePeriod;
  organizationId: string;
}

export interface TopMetricParams {
  timePeriod: TimePeriod;
  sortBy: OrderMetricBy;
}

export async function getDashboardMetrics(
  params?: DashboardMetricParams,
): Promise<DashboardMetric> {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params?.timePeriod) {
      queryParams.append('timePeriod', params.timePeriod);
    }

    const response = await apiFetch(
      `${dashboardUrl}${params?.companyId}?${queryParams.toString()}`,
    );

    if (!response) {
      throw new Error('Error fetching dashboard metrics');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    throw error;
  }
}

/**
 * Fetches dashboard metrics by ID
 * @param id Dashboard metric ID
 * @returns Dashboard metrics data for the specified ID
 */
export async function getDashboardMetricById(id: string): Promise<DashboardMetric> {
  try {
    const url = `${dashboardUrl}${id}`;

    const response = await apiFetch(url);

    if (!response) {
      throw new Error(`Could not fetch dashboard metric with ID: ${id}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching dashboard metric with ID ${id}:`, error);
    throw error;
  }
}

export async function getTopOrganizationsMetrics(
  id: string,
  filter: TopMetricParams | undefined,
): Promise<TopOrganization[]> {
  let url = `${dashboardUrl}top-organizations/${id}`;
  if (filter) {
    const queryParams = new URLSearchParams();

    // Add each parameter if it exists
    Object.entries(filter).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });

    // Get the query string
    const queryString = queryParams.toString();
    url += `?${queryString}`;
  }
  const response = await apiFetch(url);
  if (!response) {
    throw new Error(`Could not fetch top organizations metrics for ID: ${id}`);
  }

  const data = await response.json();
  return data;
}

export async function getTopCompanies(
  organizationId: string,
  filter: TopMetricParams | undefined,
): Promise<TopOrganization[]> {
  let url = `${dashboardUrl}top-companies/${organizationId}`;
  if (filter) {
    const queryParams = new URLSearchParams();

    // Add each parameter if it exists
    Object.entries(filter).forEach(([key, value]) => {
      if (value) {
        queryParams.append(key, value);
      }
    });

    // Get the query string
    const queryString = queryParams.toString();
    url += `?${queryString}`;
  }
  const response = await apiFetch(url);
  if (!response) {
    throw new Error(`Could not fetch top companies metrics for ID: ${organizationId}`);
  }
  const data = await response.json();
  return data;
}
